import { ArrowLeftOutlined } from "@ant-design/icons";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import {
  Button,
  Card,
  Form,
  Input,
  Pagination,
  Row,
  Spin,
  Table,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import DownloadTableButtons from "../../../../components/DownloadTableButtons";
import { rightAction } from "../../../../redux/actions";
import cnt from "../../../../services/definedConstant";
import { BOM } from "../../../../services/definedMethod";
import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import { apiConfig, labelConfig, staticConfig } from "./wallet.config";
import { handleFlgStatus } from "../../../../components/facilities/AccountFacility";
import StatusSelect from "../../../../components/CustomStatusSelect";

const { Text } = Typography;

const columns = [
  {
    title: "Account Number",
    dataIndex: "nubanNumber",
  },
  {
    title: "Name",
    dataIndex: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: "Parent Name",
    dataIndex: "parentName",
    sorter: (a, b) => a.parentName.localeCompare(b.parentName),
  },
  {
    title: "Product Name",
    dataIndex: "walletProductName",
  },
  {
    title: "KYC Level",
    dataIndex: "kycLevel",
  },
  {
    title: labelConfig.status,
    dataIndex: "status",
  },
];

const ManageWalletAccount = (props) => {
  const navigate = useNavigate();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;

  const [walletAccountList, setWalletAccountList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [originalList, setOriginalList] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 10;
  const dispatch = useDispatch();

  useEffect(() => {
    BOM.HasPermission("READ_SAVINGSACCOUNT") && getWalletAccount();
    BOM.HasPermission("CREATE_SAVINGSACCOUNT") &&
      rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  const getWalletAccount = () => {
    setIsLoading(true);

    const body = {
      params: { offset: 0, sortOrder: "DESC", orderBy: "id" },
      session,
    };

    BOM.FetchReqAction(body, apiConfig.WalletAccount, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      }

      const objList = res.pageItems.filter(
        (item) => item?.relationshipType?.toLowerCase() === "parent"
      );

      const tempArray = objList.map((element) => ({
        key: element?.id,
        id: element?.id,
        nubanNumber: element?.nubanNumber || "-",
        walletProductName: element?.walletProductName || "-",
        parentName: element?.parentName || "-",
        name: element?.name || "-",
        kycLevel: element?.kycLevel?.value || "-",
        status: handleFlgStatus(
          element?.savingsAccountData?.status?.value,
          {},
          true
        ),
        otherKeys: {
          statusValue: element?.savingsAccountData?.status?.value,
          accountNo: element?.nubanNumber,
        },
      }));

      setWalletAccountList(tempArray);
      setOriginalList(tempArray);
      setIsLoading(false);
    });
  };

  const onSelectItemAction = (record, selected, selectedRows) => {
    if (!selected || selectedRows.length !== 1) {
      setSelectedRow(null);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      BOM.HasPermission("CREATE_BOND") && dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }

    setSelectedRow(record);

    const isEditable = record?.otherKeys?.statusValue
      ?.toLowerCase()
      .includes("pending");

    [
      BOM.HasPermission("READ_LOANPRODUCT") && cnt.RIGHT_DETAIL_BTN,
      BOM.HasPermission("UPDATE_LOANPRODUCT") &&
        isEditable &&
        cnt.RIGHT_EDIT_BTN,
    ].forEach((item) => item && dispatch({ type: item }));
  };

  function handleOnView() {
    if (rightBtn.detailBtn.toLowerCase() === "primary") {
      navigate("/detail-wallet-account", {
        state: {
          clientName: selectedRow.clientName,
          id: selectedRow.id,
          clientId: selectedRow.clientId,
          ...selectedRow.otherKeys,
        },
      });
    }
  }

  function handleOnCreate() {
    if (rightBtn.addBtn.toLowerCase() === "primary") {
      navigate("/new-wallet-account", { ofObj: selectedRow });
    }
  }

  function handleOnEdit() {
    if (rightBtn.editBtn.toLowerCase() === "primary") {
      navigate("/edit-wallet-account", {
        state: {
          clientName: selectedRow.clientName,
          id: selectedRow.id,
          clientId: selectedRow.clientId,
          ...selectedRow.otherKeys,
        },
      });
    }
  }

  const onSearch = (formValue) => {
    setIsLoading(true);

    const body = {
      session,
      params: {
        exactMatch: false,
        query: formValue.searchKey,
        resource: "savings,clientIdentifiers",
      },
    };

    BOM.FetchReqAction(body, apiConfig.Search, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      }

      const tempArray = res.map((element) => ({
        nubanNumber: element?.nubanNumber || "-",
        walletProductName: element?.walletProductName || "-",
        parentName: element?.parentName || "-",
        name: element?.name || "-",
        kycLevel: element?.kycLevel?.value || "-",
      }));

      setWalletAccountList(tempArray);
      setOriginalList(tempArray);
      setIsLoading(false);
    });
  };

  const filteredData = walletAccountList.filter((item) => {
    const search = searchKey.toLowerCase();
    return (
      item.nubanNumber.toLowerCase().includes(search) ||
      item.name.toLowerCase().includes(search) ||
      item.parentName.toLowerCase().includes(search) ||
      item.walletProductName.toLowerCase().includes(search) ||
      item.kycLevel.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSearchInTable = (e) => {
    setSearchKey(e.target.value);
    setCurrentPage(1); // Reset to page 1 on new search
  };

  const handleFilterChange = (filtered) => {
    setWalletAccountList(filtered);
    setCurrentPage(1); // Reset to page 1 on filter change
  };

  return (
    <MainLayout
      isEnableRightSidebar={true}
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.walletItems}
    >
      <TopContent
        pageTitle={labelConfig.wallet}
        createPageTitle={labelConfig.accountListing}
      />

      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div>
              <Input
                onChange={handleSearchInTable}
                placeholder="Search"
                prefix={<SearchLg width={18} color="#667085" />}
                style={{ width: 400, borderRadius: "10px" }}
              />
            </div>
            <div style={{ marginLeft: "20px" }}>
              <StatusSelect
                statusOptions={staticConfig.accountStatusOptions}
                originalList={originalList}
                onFilteredListChange={handleFilterChange}
                useRawValue={true}
                rawValuePath="otherKeys.statusValue"
              />
            </div>
            <DownloadTableButtons
              downloadData={walletAccountList}
              columns={columns}
              fileName={"Wallet Account List"}
            />
          </Row>

          <Table
            className="bond-table"
            rowSelection={{
              onSelect: onSelectItemAction,
              hideSelectAll: true,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
            scroll={{ x: 800 }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 20,
            }}
          >
            <Button
              style={{
                backgroundColor: "white",
                borderRadius: 8,
                border: "1px solid #D0D5DD",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "600",
              }}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ArrowLeftOutlined /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />

            <Button
              style={{
                backgroundColor: "white",
                borderRadius: 8,
                border: "1px solid #D0D5DD",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "600",
              }}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage >= totalPages}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageWalletAccount);
